import { Controller } from '@hotwired/stimulus'
import { post } from '@rails/request.js'
import Choices from 'choices.js'

export default class extends Controller {
  static targets = ['select', 'ids']
  static values = { createUrl: String }

  #nameToId = new Map()

  connect() {
    this.#buildNameToIdMap()

    this.choices = new Choices(this.selectTarget, {
      shouldSort: false,
      itemSelectText: '',
      allowHTML: true,
      removeItemButton: this.selectTarget.multiple,
      duplicateItemsAllowed: false,
      addChoices: true,
      searchRenderSelectedChoices: false,
      noResultsText: 'No tags found',
      addItemText: (value) => `Create <b>${value}</b>`,
      callbackOnCreateTemplates: function () {
        return {
          item: (...args) => {
            const el = Choices.defaults.templates.item.call(this, ...args)
            const color = args[1]?.customProperties?.color
            if (color) {
              el.style.setProperty('--tag-color', color)
              el.style.setProperty('--choices-primary-color', color)
            }
            return el
          },
          choice: (...args) => {
            const el = Choices.defaults.templates.choice.call(this, ...args)
            const color = args[1]?.customProperties?.color
            if (!color) return el

            el.classList.add('choices-item-tag')
            el.style.setProperty('--tag-color', color)
            return el
          },
        }
      },
    })

    this.selectTarget.addEventListener('addItem', this.#handleAddItem)
    this.selectTarget.addEventListener('removeItem', this.#syncHiddenInputs)
    this.choices.dropdown.element.addEventListener(
      'mousedown',
      this.#handleAddChoiceClick,
      true,
    )

    this.#syncHiddenInputs()
  }

  disconnect() {
    this.choices?.dropdown.element.removeEventListener(
      'mousedown',
      this.#handleAddChoiceClick,
      true,
    )
    this.selectTarget.removeEventListener('addItem', this.#handleAddItem)
    this.selectTarget.removeEventListener('removeItem', this.#syncHiddenInputs)
    this.choices?.destroy()
  }

  #buildNameToIdMap() {
    for (const option of this.selectTarget.options) {
      if (option.dataset.tagId) {
        this.#nameToId.set(option.value, option.dataset.tagId)
      }
    }
  }

  #handleAddItem = async (event) => {
    const name = event.detail.value
    if (!name) return

    if (this.#nameToId.has(name)) {
      this.#syncHiddenInputs()
      return
    }

    const tag = await this.#createTag(name)
    if (!tag) {
      this.choices.removeActiveItemsByValue(name)
      return
    }

    this.#nameToId.set(tag.name, String(tag.id))

    // Replace the colorless auto-created choice with a colored one
    this.choices.removeChoice(name)
    this.choices.setChoices(
      [
        {
          value: tag.name,
          label: tag.name,
          selected: true,
          customProperties: { color: tag.color },
        },
      ],
      'value',
      'label',
      false,
    )

    this.#syncHiddenInputs()
  }

  // Choices.js bug: the "add choice" notice has no data-id, so
  // _handleChoiceAction bails on click. Simulate Enter instead.
  #handleAddChoiceClick = (event) => {
    if (!event.target.closest('.add-choice')) return

    event.preventDefault()
    event.stopPropagation()

    if (!this.choices.input.value?.trim()) return

    this.choices.input.element.dispatchEvent(
      new KeyboardEvent('keydown', {
        bubbles: true,
        cancelable: true,
        key: 'Enter',
        keyCode: 13,
      }),
    )
  }

  #syncHiddenInputs = () => {
    const container = this.idsTarget
    container.innerHTML = ''

    for (const name of this.choices.getValue(true)) {
      const id = this.#nameToId.get(name)
      if (!id) continue

      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'block[tag_ids][]'
      input.value = id
      container.appendChild(input)
    }
  }

  async #createTag(name) {
    if (!this.hasCreateUrlValue) return null

    try {
      const response = await post(this.createUrlValue, {
        body: { name },
        responseKind: 'json',
      })

      if (!response.ok) return null
      return await response.json
    } catch {
      return null
    }
  }
}
